import { Button, Grid, Paper, Stack, Typography } from '@mui/material';
import { useModal } from 'mui-modal-provider';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import { settingsRoutes } from '../../../../../routes';
import {
    useAiDeleteSmartReplyPlan,
    useAiSmartReplyPlan,
    useAiSmartReplyPlanItems,
} from '../../../../../sic-lib/api/hooks/useAiSmartReply';
import AxiosErrorAlert from '../../../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../../../Shared/ButtonLoading';
import DialogConfirmAction from '../../../../Shared/DialogConfirmAction/DialogConfirmAction';
import Loader from '../../../../Shared/Loader';
import { EditSmartReplyPlanDrawer } from '../EditPlan/EditSmartReplyPlanDrawer';
import { SmartReplyFlow } from './SmartReplyFlow';

interface Props {
    planId: number | undefined;
}
export function SmartReplyFlowContainer({ planId }: Props) {
    const { showModal } = useModal();
    const history = useHistory();
    const [isEditPlanOpen, setIsEditPlanOpen] = useState(false);
    const {
        data: planData,
        isPending: isLoadingPlan,
        error: planError,
    } = useAiSmartReplyPlan(planId ? planId : 0);

    const {
        data: planItemData,
        isPending: planItemsLoading,
        error: planItemsError,
    } = useAiSmartReplyPlanItems(planId ? planId : 0);

    const {
        mutate: deletePlan,
        isPending: isDeleting,
        isError: isDeletingError,
        isSuccess: isDeletingSuccess,
    } = useAiDeleteSmartReplyPlan();

    const handleBackToPlansClick = () => {
        history.push(settingsRoutes.aiSmartReply);
    };

    const handleConfirmDelete = () => {
        const modal = showModal(DialogConfirmAction, {
            title: 'Please Confirm',
            description: 'Are you sure you want to delete this plan?',
            onConfirm: () => {
                deletePlan(planId!, {
                    onSuccess: () => {
                        history.push(settingsRoutes.aiSmartReply);
                    },
                });
            },
            onCancel: () => {
                modal.hide();
            },
        });
    };

    if (isLoadingPlan && !planData) {
        return <Loader isLoading={isLoadingPlan} />;
    }

    if (!isLoadingPlan && !planData) {
        return (
            <Stack spacing={2} p={2} justifyContent="center" alignItems="center">
                <Paper sx={{ p: 2 }}>
                    <Stack spacing={2}>
                        <Typography variant="h5">Plan Not Found</Typography>
                        <Button variant="text" color="primary" onClick={handleBackToPlansClick}>
                            Back to Plans
                        </Button>
                    </Stack>
                </Paper>
            </Stack>
        );
    }

    if (!isLoadingPlan && planError) {
        return (
            <Stack spacing={2} p={2} justifyContent="center" alignItems="center">
                <AxiosErrorAlert error={planError} />
                <Button variant="text" color="primary" onClick={handleBackToPlansClick}>
                    Back to Plans
                </Button>
            </Stack>
        );
    }

    return (
        <>
            {!isLoadingPlan && planData && (
                <Grid container flexDirection="column" height="100%" width="100%">
                    <Grid
                        container
                        item
                        sx={{ bgcolor: '#fff' }}
                        p={2}
                        justifyContent="space-between"
                    >
                        <Grid item>
                            <Typography variant="h5">{planData.name}</Typography>
                        </Grid>
                        <Grid item>
                            <Stack direction="row" spacing={2}>
                                <Button variant="outlined" onClick={() => setIsEditPlanOpen(true)}>
                                    Edit Plan
                                </Button>
                                <ButtonLoading
                                    onClick={handleConfirmDelete}
                                    variant="outlined"
                                    color="error"
                                    isLoading={isDeleting}
                                    isSuccess={isDeletingSuccess}
                                    isError={isDeletingError}
                                >
                                    Delete Plan
                                </ButtonLoading>
                                <Button
                                    variant="text"
                                    color="primary"
                                    onClick={handleBackToPlansClick}
                                >
                                    Back to Plans
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                    <Grid item flexGrow={1}>
                        <Loader isLoading={planItemsLoading} />
                        {!planItemsLoading && planItemData && (
                            <ReactFlowProvider>
                                <SmartReplyFlow planItems={planItemData} plan={planData} />
                            </ReactFlowProvider>
                        )}
                    </Grid>
                </Grid>
            )}
            <EditSmartReplyPlanDrawer
                isOpen={isEditPlanOpen}
                handleClose={() => setIsEditPlanOpen(false)}
                plan={planData}
            />
        </>
    );
}
